import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../components/PaginationBar';
import { fetchAppointments, Appointment } from '../api/appointments';
import { Customer } from '../api/customers';

const PAGE_SIZE = 10;

const labelCx =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-400';
const inputCx =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-slate-50';

const AppointmentsPage: FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      searchTerm ? 350 : 0,
    );
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    let ignore = false;
    const loadAppointments = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchAppointments({
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
        });
        if (ignore) return;
        setAppointments(response.data);
        setTotal(response.meta.total);
        setTotalPages(response.meta.totalPages);
      } catch (err) {
        if (!ignore) {
          setError(
            err instanceof Error ? err.message : 'Failed to load appointments',
          );
          setAppointments([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void loadAppointments();
    return () => {
      ignore = true;
    };
  }, [page, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, fromDate, toDate]);

  const appointmentsSummary = useMemo(() => {
    if (!total) return 'No appointments found';
    const start = (page - 1) * PAGE_SIZE + (appointments.length ? 1 : 0);
    const end = (page - 1) * PAGE_SIZE + appointments.length;
    return `Showing ${start}-${end} of ${total} appointments`;
  }, [appointments.length, page, total]);

  const renderCustomerLabel = (c: Customer) => {
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return name || c.email || c.mobileNumber || 'Unnamed customer';
  };

  const canEdit = (a: Appointment) =>
    a.status !== 'completed' && a.status !== 'cancelled';

  const formatScheduledWithDelta = (iso: string) => {
    const scheduled = new Date(iso);
    const now = new Date();
    const msPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.ceil(
      (scheduled.getTime() - now.getTime()) / msPerDay,
    );
    let suffix = '';
    if (diff > 0) {
      suffix = `(${diff} day${diff === 1 ? '' : 's'} to go)`;
    } else if (diff === 0) {
      suffix = '(today)';
    } else {
      const past = Math.abs(diff);
      suffix = `(${past} day${past === 1 ? '' : 's'} ago)`;
    }
    return `${scheduled.toLocaleString()} ${suffix}`;
  };

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">
            Appointment Management
          </h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Create and manage customer appointments for workshop visits. Track
            appointment status and update details before the visit is completed.
          </p>
        </div>
        <button
          type="button"
          className="ml-auto rounded-md border border-slate-200 bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm"
          onClick={() => navigate('/appointments/new')}
        >
          + New appointment
        </button>
      </div>

      <div className="mt-2">
        <section className="rounded-2xl bg-white p-4 shadow-inner">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                id="appointment-search"
                className={`${inputCx} w-full sm:max-w-xs`}
                placeholder="Customer name, phone, or vehicle"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {(fromDate || toDate) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
                  onClick={() => {
                    setSearchTerm('');
                    setDebouncedSearch('');
                    setFromDate('');
                    setToDate('');
                    setPage(1);
                  }}
                >
                  <span>✕</span> Clear Filter
                </button>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="fromDate" className={labelCx}>
                  From date
                </label>
                <input
                  id="fromDate"
                  type="date"
                  className={inputCx}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="toDate" className={labelCx}>
                  To date
                </label>
                <input
                  id="toDate"
                  type="date"
                  className={inputCx}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-semibold">Customer</th>
                  <th className="px-5 py-3 font-semibold">Vehicle</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Scheduled at</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      className="px-5 py-6 text-center text-slate-400"
                      colSpan={6}
                    >
                      Loading appointments…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      className="px-5 py-6 text-center text-danger"
                      colSpan={6}
                    >
                      {error}
                    </td>
                  </tr>
                ) : appointments.length === 0 ? (
                  <tr>
                    <td
                      className="px-5 py-6 text-center text-slate-400"
                      colSpan={6}
                    >
                      No appointments match the selected filters.
                    </td>
                  </tr>
                ) : (
                  appointments.map((a) => {
                    const vehicleLabel = a.vehicle
                      ? [
                        a.vehicle.vehicleBrand?.name,
                        a.vehicle.vehicleModel?.name,
                        a.vehicle.vehicleRegistrationNumber,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                      : '—';
                    const locationLabel =
                      a.location?.name ??
                      a.locationId ??
                      '—';
                    const statusLabel = a.status.replace(/_/g, ' ');
                    return (
                      <tr key={a.id} className="even:bg-slate-50/70">
                        <td className="px-5 py-3 text-slate-700">
                          {a.customer ? (
                            <a
                              href={`/crm/view/${a.customer.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold text-primary hover:underline"
                            >
                              {renderCustomerLabel(a.customer)}
                            </a>
                          ) : (
                            'Customer'
                          )}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          {vehicleLabel}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          {locationLabel}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          {formatScheduledWithDelta(a.scheduledAt)}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          {statusLabel}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex gap-2">
                            {canEdit(a) ? (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                                onClick={() =>
                                  navigate(`/appointments/${a.id}/edit`)
                                }
                              >
                                Edit
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                              onClick={() =>
                                navigate(`/appointments/${a.id}`)
                              }
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <PaginationBar
              page={page}
              totalPages={totalPages}
              onPrev={() =>
                setPage((current) => Math.max(1, current - 1))
              }
              onNext={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              onSelect={(value) => setPage(value)}
              disabled={loading || totalPages === 0}
              renderSummary={<span>{appointmentsSummary}</span>}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AppointmentsPage;
